// Layout.tsx
import React from "react";
import { archivedSite } from "../objects/archivedSite";
import { ArchivedChangeType } from "../enums";
import ArchivedCard from "./archived-card";
import { OwnerManager } from "./owner-manager";
import { directoryObject } from "../objects/directoryObj";
import { MgtPerson } from "@microsoft/mgt-react";
import { useMsal } from "@azure/msal-react";
import { apiScopes } from "../authConfig";
import Swal from "sweetalert2";
import StatusCode from "status-code-enum";
import { toast } from "react-toastify";
import { errorsTypes } from "../errorTypes";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { addArchivedSiteOwner, removeArchivedSiteOwner } from "../services/resource-manager";

interface archivedResourceDetailProps {
  ownedResource: archivedSite;
  baseTabId: string;
  refreshDatasetFunction: (resourceID: string, changeType: ArchivedChangeType, value: any) => void;
}

export function ResourceDetailedArchived(props: archivedResourceDetailProps) {
  MgtPerson.config.useContactApis = true;
  const { instance, accounts } = useMsal();
  // The TooltipHost root uses display: inline by default.
  // If that's causing sizing issues or tooltip positioning issues, try overriding to inline-block.

  const accessTokenRequest = {
    scopes: apiScopes.resourceManagementScopes,
    account: accounts[0],
  };
  function addGroupOwner(ownerList: directoryObject[], resourceId: string): void {
    ownerList.forEach((currOwner) => {
      Swal.showLoading();
      instance
        .acquireTokenSilent(accessTokenRequest)
        .then(async (accessTokenResponse) => {
          const toastLeaseRenewId = toast.loading(`Adding ${currOwner.userPrincipalName}`);
          let operationResult = await addArchivedSiteOwner(
            accessTokenResponse.accessToken,
            resourceId,
            currOwner.id as string
          );
          Swal.close();
          if (
            operationResult === StatusCode.SuccessOK ||
            operationResult === StatusCode.SuccessNoContent
          ) {
            props.refreshDatasetFunction(
              resourceId,
              ArchivedChangeType.AddOwner,
              currOwner.userPrincipalName as string
            );
            let collapseButtonId = props.baseTabId.replace("tab-", "btn-expandResource");
            try {
              let buttonElement = document.getElementById(collapseButtonId);
              buttonElement?.click();
            } catch {
              console.log("Could not trigger tab collapse");
            }
            toast.update(toastLeaseRenewId, {
              render: "Operation Complete",
              type: "success",
              isLoading: false,
              autoClose: 8000,
            });
          } else {
            toast.error(errorsTypes.somethingWentWrong.errorSubtitle, {
              type: "error",
              isLoading: false,
              autoClose: 8000,
            });
          }
        })
        .catch((error) => {
          if (error instanceof InteractionRequiredAuthError) {
            Swal.close();
            Swal.fire({
              titleText: errorsTypes.sessionExpired.errorTitle,
              text: errorsTypes.sessionExpired.errorSubtitle,
              icon: "error",
              willClose: () => {
                instance.acquireTokenRedirect(accessTokenRequest);
              },
            });
          }
          console.log(error);
        });
    });
  }
  function removeGroupOwner(ownerList: directoryObject[], resourceId: string): void {
    let currentUserEmail = (window as any).CURRENT_USER_EMAIL;
    let ownerListFilterResult = ownerList.filter(
      (owner) => (owner.userPrincipalName as string) === currentUserEmail
    );
    let filteredOwners = ownerList.filter(
      (owner) => (owner.userPrincipalName as string) !== currentUserEmail
    );
    Swal.showLoading();
    instance
      .acquireTokenSilent(accessTokenRequest)
      .then(async (accessTokenResponse) => {
        // Map over the array and call the async function for each element
        const promises = filteredOwners.map(async (owner) => {
          const toastLeaseRenewId = toast.loading(`Removing ${owner.userPrincipalName}`);
          let operationResult = await removeArchivedSiteOwner(
            accessTokenResponse.accessToken,
            resourceId,
            owner.id as string
          );
          Swal.close();
          if (
            operationResult === StatusCode.SuccessOK ||
            operationResult === StatusCode.SuccessNoContent
          ) {
            props.refreshDatasetFunction(
              resourceId,
              ArchivedChangeType.RemoveOwner,
              owner.userPrincipalName as string
            );
            let collapseButtonId = props.baseTabId.replace("tab-", "btn-expandResource");
            try {
              let buttonElement = document.getElementById(collapseButtonId);
              buttonElement?.click();
            } catch {
              console.log("Could not trigger tab collapse");
            }

            toast.update(toastLeaseRenewId, {
              render: "Operation Complete",
              type: "success",
              isLoading: false,
              autoClose: 8000,
            });
          } else {
            toast.error(errorsTypes.somethingWentWrong.errorSubtitle, {
              type: "error",
              isLoading: false,
              autoClose: 8000,
            });
          }
        });
        await Promise.all(promises).then(async () => {
          if (ownerListFilterResult.length > 0) {
            const toastLeaseRenewId = toast.loading(
              `Removing ${ownerListFilterResult[0].userPrincipalName as string}`
            );
            let operationResult = await removeArchivedSiteOwner(
              accessTokenResponse.accessToken,
              resourceId,
              ownerListFilterResult[0].id as string
            );
            Swal.close();
            if (
              operationResult === StatusCode.SuccessOK ||
              operationResult === StatusCode.SuccessNoContent
            ) {
              let collapseButtonId = props.baseTabId.replace("tab-", "btn-expandResource");
              try {
                let buttonElement = document.getElementById(collapseButtonId);
                buttonElement?.click();
              } catch {
                console.log("Could not trigger tab collapse");
              }
              props.refreshDatasetFunction(
                resourceId,
                ArchivedChangeType.DeArchive,
                ownerListFilterResult[0].userPrincipalName as string
              );

              toast.update(toastLeaseRenewId, {
                render: "Operation Complete",
                type: "success",
                isLoading: false,
                autoClose: 8000,
              });
            } else {
              toast.error(errorsTypes.somethingWentWrong.errorSubtitle, {
                type: "error",
                isLoading: false,
                autoClose: 8000,
              });
            }
          }
        });
      })
      .catch((error) => {
        if (error instanceof InteractionRequiredAuthError) {
          Swal.close();
          Swal.fire({
            titleText: errorsTypes.sessionExpired.errorTitle,
            text: errorsTypes.sessionExpired.errorSubtitle,
            icon: "error",
            willClose: () => {
              instance.acquireTokenRedirect(accessTokenRequest);
            },
          });
        }
        console.log(error);
      });
  }
  return (
    <div style={{ marginLeft: "10px", marginRight: "10px" }}>
      <div className="row">
        <ul className="nav nav-tabs">
          <li className="nav-item" role="presentation" style={{ borderColor: "#E9E9E9" }}>
            <button
              type="button"
              id={props.baseTabId + "-overview"}
              className="nav-link active nav-link-resource"
              data-bs-toggle="tab"
              data-bs-target={"#" + props.baseTabId + "-overview-content"}
              role="tab"
              aria-controls="home"
              aria-selected="true"
              style={{ fontWeight: "bold" }}
            >
              <i className="intelicon-contacts-outlined" style={{ fontWeight: "bold" }}></i>{" "}
              Overview
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              type="button"
              id={props.baseTabId + "-owners"}
              className="nav-link nav-link-resource"
              data-bs-toggle="tab"
              data-bs-target={"#" + props.baseTabId + "-owners-content"}
              role="tab"
              aria-controls="profile"
              aria-selected="false"
              style={{ fontWeight: "bold" }}
            >
              <i className="intelicon-primary-user-person-solid" style={{ fontWeight: "bold" }}></i>{" "}
              Ownership
            </button>
          </li>
        </ul>
        <div
          className="tab-content border-end border-bottom border-start"
          style={{ backgroundColor: "#E9E9E9" }}
        >
          <div
            className="tab-pane fade active show "
            id={props.baseTabId + "-overview-content"}
            role="tabpanel"
            aria-labelledby="home-tab"
          >
            <ArchivedCard ownedResource={props.ownedResource} />

            <br />
          </div>
          <div
            className="tab-pane"
            id={props.baseTabId + "-owners-content"}
            role="tabpanel"
            aria-labelledby="profile-tab"
          >
            <div style={{ paddingLeft: "14px" }}>
              <br />
              <OwnerManager
                currentOwners={props.ownedResource.ownersList}
                resourceId={props.ownedResource.id}
                siteId=""
                isM365={props.ownedResource.isConnected === "Y"}
                addFunction={addGroupOwner}
                removeFunction={removeGroupOwner}
                isAGS={false}
                currentNonExistingOwners={[]}
                currentNonValidOwners={[]}
                readOnlyOwners={props.ownedResource.readOnlyOwners}
              ></OwnerManager>
              <br />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResourceDetailedArchived;
